'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import VideoCard from '@/components/VideoCard';
import SubmitVideoModal from '@/components/SubmitVideoModal';
import VideoPlayerModal from '@/components/VideoPlayerModal';
import AdminPanel from '@/components/AdminPanel';
import AdDisplay from '@/components/AdDisplay';
import PricingModal from '@/components/PricingModal';
import HomeNetworkSection from '@/components/HomeNetworkSection';
import HomeFooter from '@/components/HomeFooter';
import NavBar from '@/components/NavBar';

import { createClient } from '@/lib/supabase/client';
import {
  Video,
  MOCK_VIDEOS,
  MOCK_PENDING_VIDEOS,
  VIDEO_CATEGORIES,
  formatViews,
} from '@/data/videos';

const PAGE_SIZE = 24;

export default function HomePage() {
  const router = useRouter();
  const [approvedVideos, setApprovedVideos] = useState<Video[]>([]);
  const [pendingVideos, setPendingVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [editPostId, setEditPostId] = useState<string | undefined>(undefined);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [activeSort, setActiveSort] = useState<'newest' | 'trending' | 'most_viewed' | 'most_commented'>('newest');
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [videosLoading, setVideosLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [featuredVideo, setFeaturedVideo] = useState<Video | null>(null);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [trendingVideos, setTrendingVideos] = useState<Video[]>([]);
  const [trendingScrollIndex, setTrendingScrollIndex] = useState(0);
  const trendingRef = useRef<HTMLDivElement | null>(null);
  const [featuredPlacements, setFeaturedPlacements] = useState<Video[]>([]);
  const [featuredPlacementsLoading, setFeaturedPlacementsLoading] = useState(true);
  const featuredPlacementsRef = useRef<HTMLDivElement | null>(null);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  const mapDbVideoToVideo = (row: any): Video => ({
    id: row.id,
    title: row.title || '',
    tag: row.tag || '',
    description: row.description || '',
    source: row.source || 'youtube',
    url: row.url || '',
    embedId: row.embed_id || undefined,
    thumbnail: row.thumbnail || '',
    thumbnailAlt: row.thumbnail_alt || '',
    views: row.views || 0,
    likesCount: row.likes_count || 0,
    submittedBy: row.submitted_by || '',
    submittedAt: row.submitted_at || row.created_at?.split('T')[0] || '',
    status: row.video_status || 'approved',
    category: row.category || '',
  });

  // Load initial page of approved videos
  const fetchApprovedVideos = useCallback(async (page: number, append: boolean) => {
    if (page === 0) setVideosLoading(true);
    else setLoadingMore(true);
    try {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('video_status', 'approved')
        .order('submitted_at', { ascending: false })
        .range(from, to);
      if (error) throw error;
      const mapped = data ? data.map(mapDbVideoToVideo) : [];
      if (page === 0 && mapped.length === 0) {
        setApprovedVideos(MOCK_VIDEOS);
        setHasMore(false);
      } else {
        setApprovedVideos(prev => append ? [...prev, ...mapped] : mapped);
        setHasMore(mapped.length === PAGE_SIZE);
      }
    } catch {
      if (page === 0) {
        setApprovedVideos(MOCK_VIDEOS);
        setHasMore(false);
      }
    } finally {
      setVideosLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const fetchPendingVideos = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('video_status', 'pending')
        .order('submitted_at', { ascending: false });
      if (error) throw error;
      setPendingVideos(data ? data.map(mapDbVideoToVideo) : MOCK_PENDING_VIDEOS);
    } catch {
      setPendingVideos(MOCK_PENDING_VIDEOS);
    }
  }, []);

  const fetchFeaturedVideo = useCallback(async () => {
    setFeaturedLoading(true);
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('is_featured', true)
        .eq('video_status', 'approved')
        .limit(1);
      if (error) throw error;
      setFeaturedVideo(data && data.length > 0 ? mapDbVideoToVideo(data[0]) : null);
    } catch {
      setFeaturedVideo(null);
    } finally {
      setFeaturedLoading(false);
    }
  }, []);

  const fetchTrendingVideos = useCallback(async () => {
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const isoDate = sevenDaysAgo.toISOString();
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('video_status', 'approved')
        .gte('submitted_at', isoDate)
        .order('views', { ascending: false })
        .limit(12);
      if (error) throw error;
      const mapped = data ? data.map(mapDbVideoToVideo) : [];
      // Fall back to top-viewed all-time if not enough recent content
      if (mapped.length < 4) {
        const { data: allData } = await supabase
          .from('videos')
          .select('*')
          .eq('video_status', 'approved')
          .order('views', { ascending: false })
          .limit(12);
        setTrendingVideos(allData ? allData.map(mapDbVideoToVideo) : MOCK_VIDEOS.slice(0, 12));
      } else {
        setTrendingVideos(mapped);
      }
    } catch {
      setTrendingVideos(MOCK_VIDEOS.slice(0, 12));
    }
  }, []);

  const fetchFeaturedPlacements = useCallback(async () => {
    setFeaturedPlacementsLoading(true);
    try {
      // Get all succeeded featured-video-weekly subscriptions that have a video_id
      const { data: subs, error: subsError } = await supabase
        .from('subscriptions')
        .select('video_id, created_at')
        .eq('plan', 'featured-video-weekly')
        .eq('payment_status', 'succeeded')
        .not('video_id', 'is', null)
        .order('created_at', { ascending: false });

      if (subsError) throw subsError;

      if (!subs || subs.length === 0) {
        setFeaturedPlacements([]);
        return;
      }

      // Deduplicate video IDs (keep most recent purchase per video)
      const uniqueVideoIds = [...new Set(subs.map((s: any) => s.video_id).filter(Boolean))];

      const { data: videos, error: videosError } = await supabase
        .from('videos')
        .select('*')
        .in('id', uniqueVideoIds)
        .eq('video_status', 'approved');

      if (videosError) throw videosError;

      // Preserve purchase order
      const videoMap = new Map((videos || []).map((v: any) => [v.id, v]));
      const ordered = uniqueVideoIds
        .map(id => videoMap.get(id))
        .filter(Boolean)
        .map(mapDbVideoToVideo);

      setFeaturedPlacements(ordered);
    } catch {
      setFeaturedPlacements([]);
    } finally {
      setFeaturedPlacementsLoading(false);
    }
  }, []);

  useEffect(() => {
    setMounted(true);
    // Critical data first
    fetchApprovedVideos(0, false);
    fetchFeaturedVideo();
    // Stagger secondary fetches to avoid rate limits
    const t1 = setTimeout(() => fetchPendingVideos(), 300);
    const t2 = setTimeout(() => fetchTrendingVideos(), 1200);
    const t5 = setTimeout(() => fetchFeaturedPlacements(), 1500);
    // Check for editPost query param
    try {
      const params = new URLSearchParams(window.location.search);
      const postId = params.get('editPost');
      if (postId) {
        setEditPostId(postId);
        setShowAdminPanel(true);
        // Clean up URL without reload
        const url = new URL(window.location.href);
        url.searchParams.delete('editPost');
        window.history.replaceState({}, '', url.toString());
      }
    } catch {}
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t5);
    };
  }, []);

  // Reset pagination when category or search changes
  useEffect(() => {
    setCurrentPage(0);
    setHasMore(true);
    fetchApprovedVideos(0, false);
  }, [activeCategory, searchQuery, activeSort]);

  // Auto-rotate featured video
  useEffect(() => {
    const timer = setInterval(() => {
      setFeaturedIndex(prev => (prev + 1) % Math.min(4, approvedVideos.length));
    }, 5000);
    return () => clearInterval(timer);
  }, [approvedVideos.length]);

  const featuredVideos = approvedVideos.length > 0 ? approvedVideos.slice(0, 4) : MOCK_VIDEOS.slice(0, 4);
  const currentFeatured = featuredVideo || featuredVideos[featuredIndex] || featuredVideos[0];

  const filteredVideos = approvedVideos.filter(video => {
    const matchesCategory = activeCategory === 'All' || video.category === activeCategory;
    const matchesSearch = !searchQuery ||
      video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Apply sort to filtered videos
  const sortedFilteredVideos = [...filteredVideos].sort((a, b) => {
    switch (activeSort) {
      case 'trending':
        return b.views - a.views;
      case 'most_viewed':
        return b.views - a.views;
      case 'most_commented':
        return b.comments - a.comments;
      case 'newest':
      default:
        return b.submittedAt.localeCompare(a.submittedAt);
    }
  });

  // (date grouping removed — videos now shown as a single flat list, newest first)

  const handleVideoSubmit = useCallback((videoData: Omit<Video, 'id' | 'views' | 'comments' | 'status'>) => {
    // Refresh pending videos from DB so the admin panel shows the new submission
    fetchPendingVideos();
  }, [fetchPendingVideos]);

  const handleApprove = useCallback(async (id: string) => {
    try {
      await supabase
        .from('videos')
        .update({ video_status: 'approved' })
        .eq('id', id);
    } catch {
      // fallback: update local state only
    }
    const video = pendingVideos.find(v => v.id === id);
    if (video) {
      const approvedVideo: Video = { ...video, status: 'approved' };
      setApprovedVideos(prev => [approvedVideo, ...prev]);
      setPendingVideos(prev => prev.filter(v => v.id !== id));
    }
    fetchApprovedVideos(0, false);
    fetchPendingVideos();
  }, [pendingVideos, fetchApprovedVideos, fetchPendingVideos]);

  const handleReject = useCallback(async (id: string) => {
    try {
      await supabase
        .from('videos')
        .update({ video_status: 'rejected' })
        .eq('id', id);
    } catch {
      // fallback: update local state only
    }
    setPendingVideos(prev => prev.filter(v => v.id !== id));
    fetchPendingVideos();
  }, [fetchPendingVideos]);

  const totalViews = approvedVideos.reduce((sum, v) => sum + v.views, 0);

  // Compute trending threshold: top 20% by views, minimum 1000 views
  const trendingThreshold = (() => {
    if (approvedVideos.length === 0) return 1000;
    const sorted = [...approvedVideos].sort((a, b) => b.views - a.views);
    const top20Index = Math.max(0, Math.floor(sorted.length * 0.2) - 1);
    return Math.max(1000, sorted[top20Index]?.views ?? 1000);
  })();

  return (
    <div className="min-h-screen bg-surface-primary font-condensed">
      {/* ===== HEADER ===== */}
      <NavBar
        activePage="home"
        onMobileMenuChange={setMobileMenuOpen}
        rightSlot={
          <div className="flex items-center gap-1.5 md:gap-2 lg:gap-3">
            {/* Search */}
            <div className="relative">
              {showSearch ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search videos..."
                    className="bg-surface-secondary border border-stroke-primary text-text-primary placeholder-text-muted rounded-[4px] px-3 py-1.5 text-sm w-28 sm:w-40 md:w-56 focus:outline-none focus:border-brand-red transition-colors"
                    autoFocus
                  />
                  <button
                    onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                    className="flex items-center justify-center w-9 h-9 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-[4px] transition-colors"
                    aria-label="Close search"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowSearch(true)}
                  className="flex items-center justify-center w-10 h-10 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-[4px] transition-colors"
                  aria-label="Open search"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              )}
            </div>

            {/* Submit Video Button */}
            <button
              onClick={() => setShowSubmitModal(true)}
              className="btn-primary text-xs sm:text-sm md:text-base px-2 sm:px-3 md:px-4 py-1.5 md:py-2 flex items-center gap-1"
              aria-label="Submit a video"
            >
              <span className="hidden sm:inline">SUBMIT VIDEO</span>
              <span className="sm:hidden">+VIDEO</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>

            {/* Advertise Button */}
            <button
              onClick={() => setShowPricingModal(true)}
              className="hidden sm:flex items-center gap-1.5 border border-brand-red text-brand-red hover:bg-brand-red hover:text-white text-sm font-bold uppercase px-3 py-1.5 rounded-[4px] transition-colors"
              aria-label="Advertise on ViddStar"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a13.937 13.937 0 01-3.417.894l-2.147-6.15M15 10a3 3 0 11-6 0 3 3 0 016 0zM12 2a10 10 0 100 20 10 10 0 000-20z" />
              </svg>
              Advertise
            </button>

            {/* Admin button */}
            <button
              onClick={() => setShowAdminPanel(true)}
              className="relative flex items-center justify-center w-10 h-10 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-[4px] transition-colors"
              aria-label="Admin panel"
              title="Admin Panel"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zM12 2a10 10 0 100 20 10 10 0 000-20z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {pendingVideos.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-red text-white text-xs font-bold rounded-full flex items-center justify-center leading-none">
                  {pendingVideos.length}
                </span>
              )}
            </button>
          </div>
        }
        mobileMenuExtra={
          <>
            {/* Video categories */}
            <p className="text-text-muted text-xs uppercase tracking-widest mb-2">Categories</p>
            {['All', 'Fights & Brawls', 'Sports', 'Music', 'Funny & Viral', 'Entertainment', 'News & Politics'].map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setMobileMenuOpen(false); }}
                className={`w-full text-left text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] transition-colors duration-150 ${
                  activeCategory === cat ? 'text-brand-red' : 'text-text-secondary hover:text-text-primary hover:bg-surface-tertiary'
                }`}
              >
                {cat === 'All' ? 'All Videos' : cat}
              </button>
            ))}
            <div className="mt-1 pt-1 border-t border-stroke-primary flex flex-col gap-0.5">
              <button
                onClick={() => { setShowPricingModal(true); setMobileMenuOpen(false); }}
                className="w-full text-left text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] text-brand-red hover:bg-surface-tertiary transition-colors duration-150"
              >
                📣 Advertise / Promote
              </button>
              <button
                onClick={() => { setShowSubmitModal(true); setMobileMenuOpen(false); }}
                className="w-full text-left text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] text-text-secondary hover:text-text-primary hover:bg-surface-tertiary transition-colors duration-150"
              >
                Submit a Video
              </button>
              <button
                onClick={() => { setShowAdminPanel(true); setMobileMenuOpen(false); }}
                className="w-full text-left text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] text-text-muted hover:text-brand-red hover:bg-surface-tertiary transition-colors duration-150"
              >
                Admin Panel
              </button>
              <Link
                href="/advertiser"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] text-text-muted hover:text-brand-red hover:bg-surface-tertiary transition-colors duration-150"
              >
                📊 My Ad Campaigns
              </Link>
              <Link
                href="/ad-revenue"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] text-text-muted hover:text-brand-red hover:bg-surface-tertiary transition-colors duration-150"
              >
                💳 Ad Revenue & Billing
              </Link>
            </div>
          </>
        }
      />

      {/* ===== MAIN CONTENT ===== */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">

        {/* ===== BANNER AD ===== */}
        <AdDisplay placement="banner" className="mb-4" />

        {/* ===== FEATURED HERO SECTION ===== */}
        {!searchQuery && activeCategory === 'All' && (
          <section className="mb-6">
            {videosLoading && !currentFeatured ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-stroke-primary animate-pulse">
                <div className="lg:col-span-2 bg-surface-tertiary" style={{ aspectRatio: '16/9' }} />
                <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-stroke-primary flex flex-col">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="flex gap-3 p-3 border-b border-stroke-primary last:border-b-0">
                      <div className="flex-shrink-0 w-24 bg-surface-secondary rounded" style={{ aspectRatio: '16/9' }} />
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-3 bg-surface-secondary rounded w-full" />
                        <div className="h-3 bg-surface-secondary rounded w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-stroke-primary">
              {/* Main featured video */}
              <div
                className="lg:col-span-2 relative cursor-pointer group overflow-hidden bg-surface-tertiary"
                onClick={() => currentFeatured && router.push(`/video/${currentFeatured.id}`)}
                style={{ aspectRatio: '16/9' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && currentFeatured && router.push(`/video/${currentFeatured.id}`)}
                aria-label={currentFeatured ? `Watch featured: ${currentFeatured.title}` : 'Featured video'}
              >
                {currentFeatured && (
                  <>
                    <img
                      src={currentFeatured.thumbnail}
                      alt={currentFeatured.thumbnailAlt}
                      className="w-full h-full object-cover group-hover:brightness-75 transition-all duration-500"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    {/* Play button */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 bg-brand-red bg-opacity-90 rounded-full flex items-center justify-center shadow-xl">
                        <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                    {/* Title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                      <h2 className="text-text-primary text-xl md:text-3xl font-normal leading-tight">
                        {currentFeatured.tag && (
                          <span className="block text-brand-red font-bold text-lg md:text-2xl mb-1">
                            {currentFeatured.tag}
                          </span>
                        )}
                        {currentFeatured.title}
                      </h2>
                    </div>
                  </>
                )}
              </div>

              {/* Side featured list — horizontal scroll strip on mobile, vertical list on desktop */}
              <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-stroke-primary featured-side-list lg:flex lg:flex-col">
                {featuredVideos.map((video, i) => (
                  <div
                    key={video.id}
                    className={`flex gap-3 p-3 cursor-pointer group transition-colors duration-150 border-b border-stroke-primary last:border-b-0 ${
                      i === featuredIndex ? 'bg-surface-secondary' : 'hover:bg-surface-secondary'
                    }`}
                    onClick={() => { setFeaturedIndex(i); router.push(`/video/${video.id}`); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && router.push(`/video/${video.id}`)}
                  >
                    <div className="flex-shrink-0 w-24 relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
                      <img
                        src={video.thumbnail}
                        alt={video.thumbnailAlt}
                        className="w-full h-full object-cover group-hover:brightness-75 transition-all duration-300"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-text-primary text-sm leading-snug line-clamp-3">
                        {video.tag && <span className="tag-label mr-1 text-xs">{video.tag} </span>}
                        {video.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-text-secondary text-xs">
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {formatViews(video.views)}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-7.44 9-7s9 3.582 9 8z" />
                            </svg>
                          {video.comments}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
          </section>
        )}

        {/* ===== TRENDING VIDEOS CAROUSEL ===== */}
        {!searchQuery && activeCategory === 'All' && trendingVideos.length > 0 && (
          <section className="mb-8">
            {/* Section header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="section-header">
                  <span className="text-text-primary text-2xl md:text-3xl font-normal">TRENDING</span>
                  <span className="text-brand-red font-bold text-2xl md:text-3xl">THIS WEEK</span>
                </div>
                <span className="flex items-center gap-1 bg-brand-red text-white text-xs font-bold uppercase px-2 py-0.5 rounded-[2px]">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  Hot
                </span>
              </div>
              {/* Scroll arrows — hidden on touch devices via .carousel-arrows CSS class */}
              <div className="flex items-center gap-1 carousel-arrows">
                <button
                  onClick={() => {
                    if (trendingRef.current) {
                      trendingRef.current.scrollBy({ left: -320, behavior: 'smooth' });
                    }
                  }}
                  className="w-8 h-8 flex items-center justify-center bg-surface-tertiary border border-stroke-primary hover:border-brand-red hover:text-brand-red text-text-secondary rounded-[4px] transition-colors"
                  aria-label="Scroll trending left"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    if (trendingRef.current) {
                      trendingRef.current.scrollBy({ left: 320, behavior: 'smooth' });
                    }
                  }}
                  className="w-8 h-8 flex items-center justify-center bg-surface-tertiary border border-stroke-primary hover:border-brand-red hover:text-brand-red text-text-secondary rounded-[4px] transition-colors"
                  aria-label="Scroll trending right"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Scrollable carousel */}
            <div
              ref={trendingRef}
              className="carousel-scroll flex gap-3 sm:gap-4 overflow-x-auto pb-2"
            >
              {trendingVideos.map((video, idx) => (
                <div
                  key={video.id}
                  className="carousel-item flex-shrink-0 w-[72vw] max-w-[280px] sm:w-56 md:w-64 cursor-pointer group"
                  onClick={() => router.push(`/video/${video.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && router.push(`/video/${video.id}`)}
                  aria-label={`Watch trending: ${video.title}`}
                >
                  {/* Thumbnail */}
                  <div className="relative overflow-hidden bg-surface-tertiary rounded-[2px] border border-stroke-primary border-opacity-40" style={{ aspectRatio: '16/9' }}>
                    <img
                      src={video.thumbnail || 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=640&h=360&fit=crop'}
                      alt={video.thumbnailAlt || video.title}
                      className="w-full h-full object-cover group-hover:brightness-75 transition-all duration-300"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=640&h=360&fit=crop';
                      }}
                      loading="lazy"
                    />
                    {/* Rank badge */}
                    <div className="absolute top-2 left-2 w-6 h-6 bg-brand-red text-white text-xs font-bold flex items-center justify-center rounded-[2px]">
                      {idx + 1}
                    </div>
                    {/* Play overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-10 h-10 bg-brand-red bg-opacity-90 rounded-full flex items-center justify-center shadow-lg">
                        <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-7.44 9-7s9 3.582 9 8z" />
                        </svg>
                      </div>
                    </div>
                    {/* Views badge */}
                    <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded-[2px]">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {formatViews(video.views)}
                    </div>
                  </div>
                  {/* Title */}
                  <div className="pt-2">
                    <h3 className="text-text-primary text-sm leading-snug line-clamp-2 group-hover:text-brand-red transition-colors duration-150">
                      {video.title}
                    </h3>
                    {video.category && (
                      <span className="text-text-muted text-xs uppercase tracking-wide mt-0.5 block">{video.category}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ===== SIDEBAR AD (full-width between sections) ===== */}
        {!searchQuery && activeCategory === 'All' && (
          <AdDisplay placement="sidebar" className="mb-8" />
        )}

        {/* ===== FEATURED PLACEMENTS SECTION ===== */}
        {!searchQuery && activeCategory === 'All' && (featuredPlacementsLoading || featuredPlacements.length > 0) && (
          <section className="mb-8">
            {/* Section header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="section-header">
                  <span className="text-text-primary text-2xl md:text-3xl font-normal">FEATURED</span>
                  <span className="text-brand-red font-bold text-2xl md:text-3xl">VIDEOS</span>
                </div>
                <span className="flex items-center gap-1 bg-yellow-500 text-black text-xs font-bold uppercase px-2 py-0.5 rounded-[2px]">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  Sponsored
                </span>
              </div>
              {/* Scroll arrows — hidden on touch devices via .carousel-arrows CSS class */}
              {featuredPlacements.length > 3 && (
                <div className="flex items-center gap-1 carousel-arrows">
                  <button
                    onClick={() => {
                      if (featuredPlacementsRef.current) {
                        featuredPlacementsRef.current.scrollBy({ left: -320, behavior: 'smooth' });
                      }
                    }}
                    className="w-8 h-8 flex items-center justify-center bg-surface-tertiary border border-stroke-primary hover:border-brand-red hover:text-brand-red text-text-secondary rounded-[4px] transition-colors"
                    aria-label="Scroll featured left"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      if (featuredPlacementsRef.current) {
                        featuredPlacementsRef.current.scrollBy({ left: 320, behavior: 'smooth' });
                      }
                    }}
                    className="w-8 h-8 flex items-center justify-center bg-surface-tertiary border border-stroke-primary hover:border-brand-red hover:text-brand-red text-text-secondary rounded-[4px] transition-colors"
                    aria-label="Scroll featured right"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {featuredPlacementsLoading ? (
              <div className="flex gap-3 sm:gap-4 overflow-x-hidden">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex-shrink-0 w-[72vw] max-w-[280px] sm:w-56 md:w-64 animate-pulse">
                    <div className="bg-surface-tertiary rounded-[2px] w-full" style={{ aspectRatio: '16/9' }} />
                    <div className="mt-2 h-4 bg-surface-tertiary rounded w-3/4" />
                    <div className="mt-1 h-3 bg-surface-tertiary rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : (
              <div
                ref={featuredPlacementsRef}
                className="carousel-scroll flex gap-3 sm:gap-4 overflow-x-auto pb-2"
              >
                {featuredPlacements.map((video) => (
                  <div
                    key={video.id}
                    className="carousel-item flex-shrink-0 w-[72vw] max-w-[280px] sm:w-56 md:w-64 cursor-pointer group"
                    onClick={() => router.push(`/video/${video.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && router.push(`/video/${video.id}`)}
                    aria-label={`Watch featured: ${video.title}`}
                  >
                    {/* Thumbnail */}
                    <div className="relative overflow-hidden bg-surface-tertiary border border-yellow-500 border-opacity-40" style={{ aspectRatio: '16/9' }}>
                      <img
                        src={video.thumbnail || 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=640&h=360&fit=crop'}
                        alt={video.thumbnailAlt || video.title}
                        className="w-full h-full object-cover group-hover:brightness-75 transition-all duration-300"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=640&h=360&fit=crop';
                        }}
                        loading="lazy"
                      />
                      {/* Sponsored badge */}
                      <div className="absolute top-2 left-2 flex items-center gap-1 bg-yellow-500 text-black text-xs font-bold px-1.5 py-0.5 rounded-[2px]">
                        <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                        Featured
                      </div>
                      {/* Play overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-10 h-10 bg-brand-red bg-opacity-90 rounded-full flex items-center justify-center shadow-lg">
                          <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-7.44 9-7s9 3.582 9 8z" />
                          </svg>
                        </div>
                      </div>
                      {/* Views badge */}
                      <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded-[2px]">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {formatViews(video.views)}
                      </div>
                    </div>
                    {/* Title */}
                    <div className="pt-2">
                      <h3 className="text-text-primary text-sm leading-snug line-clamp-2 group-hover:text-brand-red transition-colors duration-150">
                        {video.title}
                      </h3>
                      {video.category && (
                        <span className="text-text-muted text-xs uppercase tracking-wide mt-0.5 block">{video.category}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ===== SEARCH RESULTS HEADER ===== */}
        {searchQuery && (
          <div className="mb-4">
            <div className="section-header">
              <span className="text-text-primary text-2xl font-normal">SEARCH</span>
              <span className="text-brand-red font-bold text-2xl">RESULTS</span>
              <span className="text-text-muted text-sm ml-2">{filteredVideos.length} videos found for "{searchQuery}"</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {sortedFilteredVideos.map(video => (
                <VideoCard key={video.id} video={video} isTrending={video.views >= trendingThreshold} />
              ))}
            </div>
            {filteredVideos.length === 0 && (
              <div className="text-center py-12">
                <p className="text-text-secondary text-lg">No videos found for "{searchQuery}"</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-brand-red text-sm mt-2 hover:underline"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        )}

        {/* ===== VIDEO GRID BY DATE ===== */}
        {!searchQuery && (
          <div className="space-y-8">
            {/* Sort + Category Filter Bar */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 pb-3 border-b border-stroke-primary">
              {/* Category pills */}
              <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0 w-full sm:w-auto">
                {['All', 'Fights & Brawls', 'Sports', 'Music', 'Funny & Viral', 'Entertainment', 'News & Politics'].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-[4px] transition-colors duration-150 whitespace-nowrap min-h-[36px] ${
                      activeCategory === cat
                        ? 'bg-brand-red text-white' :'bg-surface-secondary text-text-secondary hover:text-text-primary hover:bg-surface-tertiary border border-stroke-primary'
                    }`}
                  >
                    {cat === 'All' ? 'All' : cat}
                  </button>
                ))}
              </div>
              {/* Sort toggles */}
              <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
                <span className="text-text-muted text-xs uppercase tracking-widest mr-1 hidden sm:block">Sort:</span>
                {([
                  { key: 'newest', label: 'Newest' },
                  { key: 'trending', label: '🔥 Trending' },
                  { key: 'most_viewed', label: '👁 Views' },
                  { key: 'most_commented', label: '💬 Comments' },
                ] as { key: 'newest' | 'trending' | 'most_viewed' | 'most_commented'; label: string }[]).map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setActiveSort(key)}
                    className={`text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-[4px] transition-colors duration-150 whitespace-nowrap min-h-[36px] ${
                      activeSort === key
                        ? 'bg-surface-tertiary text-brand-red border border-brand-red' :'bg-surface-secondary text-text-secondary hover:text-text-primary hover:bg-surface-tertiary border border-stroke-primary'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {videosLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-6 sm:gap-x-4 sm:gap-y-7">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-surface-tertiary rounded-[4px] w-full" style={{ aspectRatio: '16/9' }} />
                    <div className="mt-2 h-4 bg-surface-tertiary rounded w-3/4" />
                    <div className="mt-1 h-3 bg-surface-tertiary rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* ===== LATEST VIDEOS SECTION HEADER ===== */}
                <section>
                  <div className="flex items-center justify-between border-b border-stroke-primary pb-3 mb-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-text-primary text-xl sm:text-2xl md:text-3xl font-normal">LATEST</span>
                      <span className="text-brand-red font-bold text-xl sm:text-2xl md:text-3xl">VIDEOS</span>
                    </div>
                    <span className="text-text-muted text-xs sm:text-sm">{sortedFilteredVideos.length} video{sortedFilteredVideos.length !== 1 ? 's' : ''}</span>
                  </div>

                  {/* Flat Video Grid — newest to oldest */}
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-6 sm:gap-x-4 sm:gap-y-7 video-grid-responsive">
                    {sortedFilteredVideos.map((video, vidIndex) => (
                      <React.Fragment key={video.id}>
                        <VideoCard video={video} isTrending={video.views >= trendingThreshold} />
                        {/* Between-videos ad: after every 8th video */}
                        {(vidIndex + 1) % 8 === 0 && vidIndex !== sortedFilteredVideos.length - 1 && (
                          <div className="col-span-2 md:col-span-3 lg:col-span-4">
                            <AdDisplay placement="between-videos" />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </section>

                {/* ===== FEATURED VIDEO (after first 8 videos) ===== */}
                {(featuredLoading || featuredVideo) && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between border-b border-stroke-primary pb-3 mb-4">
                      <div className="flex items-baseline gap-2">
                        <span className="text-text-primary text-2xl md:text-3xl font-normal">FEATURED</span>
                        <span className="text-brand-red font-bold text-2xl md:text-3xl">VIDEO</span>
                      </div>
                      {featuredVideo && (
                        <span className="flex items-center gap-1 text-yellow-400 text-xs font-bold uppercase tracking-wide">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          Editor's Pick
                        </span>
                      )}
                    </div>

                    {featuredLoading ? (
                      <div className="animate-pulse max-w-2xl">
                        <div className="bg-surface-tertiary rounded-[4px] w-full" style={{ aspectRatio: '16/9' }} />
                        <div className="mt-3 h-5 bg-surface-tertiary rounded w-2/3" />
                        <div className="mt-2 h-4 bg-surface-tertiary rounded w-1/3" />
                      </div>
                    ) : featuredVideo ? (
                      <div
                        className="group cursor-pointer max-w-2xl"
                        onClick={() => router.push(`/video/${featuredVideo.id}`)}
                      >
                        <div className="relative overflow-hidden bg-surface-tertiary border border-stroke-primary group-hover:border-brand-red transition-colors duration-200" style={{ aspectRatio: '16/9' }}>
                          {featuredVideo.thumbnail ? (
                            <img
                              src={featuredVideo.thumbnail}
                              alt={featuredVideo.thumbnailAlt || featuredVideo.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-surface-tertiary">
                              <svg className="w-16 h-16 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.069A1 1 0 0021 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="w-16 h-16 bg-brand-red rounded-full flex items-center justify-center shadow-lg">
                              <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                          {featuredVideo.category && (
                            <div className="absolute top-3 left-3">
                              <span className="bg-surface-secondary text-brand-red text-xs font-bold uppercase px-2 py-0.5 border border-brand-red">
                                {featuredVideo.category}
                              </span>
                            </div>
                          )}
                          <div className="absolute bottom-3 right-3">
                            <span className="bg-black/70 text-white text-xs font-bold px-2 py-0.5 rounded-[2px] flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              {formatViews(featuredVideo.views)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3">
                          {featuredVideo.tag && (
                            <span className="tag-label text-xs mr-2">{featuredVideo.tag}</span>
                          )}
                          <h2 className="text-text-primary font-bold text-lg md:text-xl leading-snug group-hover:text-brand-red transition-colors duration-150 mt-1">
                            {featuredVideo.title}
                          </h2>
                          {featuredVideo.description && (
                            <p className="text-text-secondary text-sm mt-1 line-clamp-2">{featuredVideo.description}</p>
                          )}
                          <p className="text-text-muted text-xs mt-1">Submitted by {featuredVideo.submittedBy || 'ViddStar'}</p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}

                {filteredVideos.length === 0 && (
                  <div className="text-center py-16">
                    <svg className="w-16 h-16 text-text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.069A1 1 0 0021 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <p className="text-text-secondary text-xl mb-2">No videos in this category yet</p>
                    <p className="text-text-muted text-base mb-4">Be the first to submit a video!</p>
                    <button onClick={() => setShowSubmitModal(true)} className="btn-primary">
                      Submit Video
                    </button>
                  </div>
                )}

                {/* Infinite scroll sentinel */}
                <div ref={loadMoreRef} className="h-4" />

                {/* Loading more indicator */}
                {loadingMore && (
                  <div className="flex items-center justify-center py-6 gap-3">
                    <div className="w-5 h-5 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
                    <span className="text-text-muted text-sm uppercase tracking-wide">Loading more videos...</span>
                  </div>
                )}

                {/* End of catalog indicator */}
                {!hasMore && filteredVideos.length > 0 && (
                  <div className="flex items-center justify-center py-6">
                    <span className="text-text-muted text-xs uppercase tracking-widest border-t border-stroke-primary pt-4 w-full text-center">
                      — All videos loaded —
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ===== STATS BANNER ===== */}
        <div className="mt-12 border border-stroke-primary bg-surface-secondary">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-stroke-primary">
            {[
              { label: 'Total Videos', value: approvedVideos.length.toLocaleString() },
              { label: 'Total Views', value: formatViews(totalViews) },
              { label: 'Pending Review', value: pendingVideos.length.toString() },
              { label: 'Categories', value: VIDEO_CATEGORIES.length.toString() },
            ].map((stat) => (
              <div key={stat.label} className="px-3 sm:px-4 md:px-6 py-4 sm:py-5 text-center">
                <div className="text-brand-red font-bold text-xl sm:text-2xl md:text-3xl">{stat.value}</div>
                <div className="text-text-muted text-xs uppercase tracking-wide mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== VIDDSTAR NETWORK SECTION ===== */}
        <HomeNetworkSection onCategoryChange={setActiveCategory} />

        {/* ===== CODE RED INTERVIEWS PARTNER SECTION ===== */}
        {/* Moved to HomeNetworkSection component */}

        {/* ===== SUBMIT CTA ===== */}
        <div className="mt-6 mb-8 border border-stroke-primary bg-surface-secondary p-4 sm:p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h2 className="text-text-primary text-xl sm:text-2xl md:text-3xl font-normal mb-1">
                GOT A <span className="text-brand-red font-bold">VIRAL CLIP?</span>
              </h2>
              <p className="text-text-secondary text-sm sm:text-base">
                Submit your video via YouTube link, Vimeo link, or direct upload. Our team reviews every submission.
              </p>
            </div>
            <button
              onClick={() => setShowSubmitModal(true)}
              className="btn-primary flex-shrink-0 text-base sm:text-lg px-5 sm:px-6 py-2.5 sm:py-3 w-full sm:w-auto"
            >
              SUBMIT YOUR VIDEO
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
      </main>

      <HomeFooter
        onCategoryChange={setActiveCategory}
        onSubmitVideo={() => setShowSubmitModal(true)}
        onAdminPanel={() => setShowAdminPanel(true)}
        onAdvertise={() => setShowPricingModal(true)}
      />

      {/* ===== MODALS ===== */}
      <SubmitVideoModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onSubmit={handleVideoSubmit}
      />

      <VideoPlayerModal
        video={selectedVideo}
        onClose={() => setSelectedVideo(null)}
        allVideos={approvedVideos}
        onVideoSelect={(v) => setSelectedVideo(v)}
      />

      {showAdminPanel && (
        <AdminPanel
          pendingVideos={pendingVideos}
          onApprove={handleApprove}
          onReject={handleReject}
          onClose={() => { setShowAdminPanel(false); setEditPostId(undefined); }}
          initialEditPostId={editPostId}
        />
      )}

      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />
    </div>
  );
}
